<template>
  <transition name="pdl-toast-fade" @before-leave="onClose()" @after-leave="$emit('destroy')">
    <div
      v-show="visible"
      :class="['pdl-toast', customClass, toastType, toastClasses, horizontalClass, {'has-close': showClose}]"
      :style="positionStyle"
      :qaid="qaid"
      role="alert"
      @mouseenter="clearTimer()"
      @mouseleave="startTimer()"
      @click="click"
    >
      <pdl-icon
        v-if="iconName || iconClass || icon"
        class="pdl-toast__icon"
        :name="iconName"
        :class="[typeClass, iconClass]"
      />
      <div class="pdl-toast__group" :class="[{'is-with-icon': typeClass || iconClass}]">
        <div v-show="message" class="pdl-toast__content">
          <h2 v-if="title.length > 0" class="pdl-toast__title" v-text="title"></h2>
          <slot>
            <div class="flex justify-between">
              <p v-if="!dangerouslyUseHTMLString" class="flex">{{ message }}</p>
              <p v-else v-html="message"></p>
              <a
                v-if="actionClick"
                role="button"
                :class="['underline hover:no-underline', actionClickColor]"
                @click.prevent="onActionClick"
                v-html="actionClickText"
              />
            </div>
          </slot>
        </div>
        <div v-if="action" class="pdl-toast__actions">
          <slot name="action">
            <p v-if="!dangerouslyUseHTMLString">{{ action }}</p>
            <p v-else v-html="action"></p>
          </slot>
        </div>
        <div v-if="link" class="pdl-toast__actions">
          <a class="underline" :href="link.target">{{ link.basePropertyValue }}</a>
        </div>
        <pdl-button
          v-if="showClose"
          class="pdl-toast__closeBtn"
          text
          icon-only
          icon="close"
          label="Close"
          @click="close"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import {computed, onMounted, defineExpose, defineEmits, defineOptions, defineProps, ref, onBeforeUnmount} from 'vue';
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlButton} from '@pedal/pdl-button';
import {PdlPopupManagerUtil} from '@pedal/pdl-shared-libraries';

defineEmits({destroy: () => true});

const toastClassesMap = {
  info: 'bg-primary-3 border border-primary-6 text-primary-9',
  success: 'bg-success-3 border border-success-6 text-success-9',
  warning: 'bg-warning-3 border border-warning-6 text-warning-9',
  error: 'bg-alert-3 border border-alert-6 text-alert-9',
};

const typeMap = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

const iconMap = {
  info: 'info',
  success: 'check_circle',
  warning: 'warning',
  error: 'error',
};

const visible = ref(false);
const timer = ref(null);

const props = defineProps({
  /**
   * Title of toast.
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * Message of toast.
   */
  message: {default: ''},
  /**
   * Action of toast.
   */
  action: {
    type: String,
    default: '',
  },
  /**
   * Text for action (alt) of toast.
   */
  actionClickText: {
    type: String,
    default: null,
  },
  /**
   * Whether to display action (alt) of toast.
   */
  actionClick: {
    type: Function,
    default: undefined,
  },
  /**
   * Duration before close. It will not automatically close if set `0`.
   */
  duration: {
    type: Number,
    default: 4500,
  },
  /**
   * Display a close button.
   */
  showClose: {
    type: Boolean,
    default: false,
  },
  /**
   * Custom class for toast.
   */
  customClass: {
    type: String,
    default: '',
  },
  /**
   * Custom icon.
   */
  icon: {
    type: String,
    default: '',
  },
  /**
   * Custom icon's class.
   */
  iconClass: {
    type: String,
    default: '',
  },
  /**
   * Callback function when closed.
   */
  onClose: {
    type: Function,
    default: null,
  },
  /**
   * Callback function when toast clicked.
   */
  onClick: {
    type: Function,
    default: null,
  },
  /**
   * Offset from the top edge of the screen. Every toast instance of the same moment should have the same offset.
   */
  verticalOffset: {
    type: Number,
    default: 0,
  },
  /**
   * Amount of time toast has been open (compared to duration).
   */
  id: {
    type: String,
    default: '',
  },
  /**
   * Toast kind.
   */
  type: {
    type: String,
    default: 'info',
  },
  /**
   * Whether message or action is treated as HTML string.
   */
  dangerouslyUseHTMLString: {
    type: Boolean,
    default: false,
  },
  /**
   * Custom position.
   */
  position: {
    type: String,
    default: 'top-right',
  },
  /**
   * Apply link to toast.
   */
  link: {
    type: Object,
    default: null,
  },
  qaid: {
    type: String,
    default: 'pdl-toast',
  },
  zIndex: {
    type: Number,
    default: 0,
  },
});

defineOptions({name: 'PdlToast'});

const typeClasses = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

const typeClass = computed(() => {
  return props.type && typeMap[props.type] ? typeClasses[typeMap[props.type]] : '';
});

const iconName = computed(() => {
  if (props.icon.length > 0) {
    return props.icon;
  }
  return props.type && iconMap[props.type] ? `${iconMap[props.type]}` : '';
});

const toastType = computed(() => {
  return props.type && typeMap[props.type] ? `pdl-toast--${typeMap[props.type]}` : '';
});

const toastClasses = computed(() => {
  return props.type && typeMap[props.type] ? `${toastClassesMap[props.type]}` : '';
});

const actionClickColor = computed(() => {
  return props.type && typeMap[props.type] ? `text-${typeMap[props.type]}` : '';
});

const horizontalClass = computed(() => {
  return props.position.indexOf('right') > -1 ? 'right' : 'left';
});

const verticalProperty = computed(() => {
  return /^top-/.test(props.position) ? 'top' : 'bottom';
});

const positionStyle = computed(() => {
  return {
    [verticalProperty.value]: `${props.verticalOffset}px`,
    zIndex: PdlPopupManagerUtil.nextZIndex() + 16000000,
  };
});

const click = () => {
  if (typeof props.onClick === 'function') {
    props.onClick();
  }
};

const onActionClick = () => {
  if (typeof props.actionClick === 'function') {
    props.actionClick();
    close();
  }
};

const close = () => {
  visible.value = false;
};

const clearTimer = () => {
  clearTimeout(timer.value);
};

const startTimer = () => {
  if (props.duration > 0) {
    timer.value = setTimeout(() => {
      if (visible.value) close();
    }, props.duration);
  }
};

const keydown = (e) => {
  if (e.keyCode === 46 || e.keyCode === 8) {
    clearTimer();
  } else if (e.keyCode === 27) {
    if (visible.value) {
      close();
    }
  } else {
    startTimer();
  }
};

onMounted(() => {
  startTimer();
  visible.value = true;
  document.addEventListener('keydown', keydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', keydown);
});
defineExpose({
  visible,
  /** @description close notification */
  close,
});
</script>
<style lang="scss">
@import 'Toast.scss';
</style>
